import Carregando from '../components/Carregando';
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  Button, TextField, Snackbar,
  LinearProgress, Alert, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PaidIcon from '@mui/icons-material/Paid';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MapIcon from '@mui/icons-material/Map';
import DescriptionIcon from '@mui/icons-material/Description';
import ConstructionIcon from '@mui/icons-material/Construction';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircle';
import Autocomplete from '@mui/material/Autocomplete';
import SecaoFormulario from '../components/SecaoFormulario';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import GaleriaFotos from '../components/GaleriaFotos';
import { formatDias } from '../utils/formatters';
import RelatorioVisita from '../components/RelatorioVisita';
import AbasProjeto from '../components/projeto/AbasProjeto';
import EvolucaoIndicador from '../components/projeto/EvolucaoIndicador';
import AcompanhamentoProjeto from '../components/projeto/AcompanhamentoProjeto';
import PlanejamentoProjeto, { PlanejamentoNoEditar } from '../components/PlanejamentoCarga';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
dayjs.locale('pt-br');

const statusConfig = {
  planejamento: { label: 'Planejamento', color: 'default'  },
  em_andamento: { label: 'Em andamento', color: 'primary'  },
  pausado:      { label: 'Pausado',      color: 'warning'  },
  concluido:    { label: 'Concluído',    color: 'success'  },
  cancelado:    { label: 'Cancelado',    color: 'error'    },
};

const statusHex = {
  planejamento: '#94A3B8',
  em_andamento: '#2563EB',
  pausado:      '#D97706',
  concluido:    '#16A34A',
  cancelado:    '#DC2626',
};

function AnelProgresso({
  pct, texto, label = 'carga', tamanho = 92, espessura = 9,
  corAnel = '#e94560', corTrilha = 'rgba(255,255,255,0.14)',
  corFundoInterno = '#0d2642', corTexto = 'white', corLabel = 'rgba(255,255,255,0.5)',
}) {
  return (
    <Box
      sx={{
        width: tamanho, height: tamanho, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(${corAnel} ${pct * 3.6}deg, ${corTrilha} 0deg)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: tamanho - espessura * 2, height: tamanho - espessura * 2, borderRadius: '50%',
          backgroundColor: corFundoInterno,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
        }}
      >
        <Typography sx={{ color: corTexto, fontWeight: 700, fontSize: 19, fontFamily: 'monospace', lineHeight: 1 }}>
          {texto ?? `${pct}%`}
        </Typography>
        <Typography sx={{ color: corLabel, fontSize: 9, mt: 0.3 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function MiniStat({ label, valor, cor = '#1a1a2e' }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', fontWeight: 700, mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 700, color: cor }}>
        {valor}
      </Typography>
    </Box>
  );
}

function BarraComparativa({ realizado, planejado, total, cor }) {
  const pctRealizado = total > 0 ? Math.min((realizado / total) * 100, 100) : 0;
  const pctPlanejado = total > 0 ? Math.min((planejado / total) * 100, 100) : 0;
  return (
    <Box sx={{ position: 'relative', mt: 2.5 }}>
      <Box sx={{ position: 'relative', height: 26, borderRadius: 1.5, backgroundColor: '#eef0f3', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${pctRealizado}%`, backgroundColor: cor, borderRadius: 1.5,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute', left: `${pctPlanejado}%`, top: -4, bottom: -4,
          width: 2, backgroundColor: '#1a1a2e', transform: 'translateX(-1px)',
        }}
      />
      <Typography
        sx={{
          position: 'absolute', left: `${pctPlanejado}%`, top: -18,
          transform: 'translateX(-50%)', fontSize: 10, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap',
        }}
      >
        Planejado
      </Typography>
    </Box>
  );
}

// Sentido escolhido no cadastro; indicadores antigos (sem escolha) deduzem pela baseline e pela meta.
function maiorEhMelhorDo(ind) {
  if (ind.sentido) return ind.sentido === 'maior';
  const temBase = ind.baseline !== null && ind.baseline !== undefined && ind.baseline !== '';
  const temMeta = ind.meta !== null && ind.meta !== undefined && ind.meta !== '';
  return !(temBase && temMeta && Number(ind.meta) < Number(ind.baseline));
}

function calcularProgresso(ind, ultima) {
  let maiorEhMelhor = maiorEhMelhorDo(ind);
  if (!ultima || !ind.meta) return { pct: null, pctExibido: 0, cor: '#94A3B8', maiorEhMelhor };
  const meta = Number(ind.meta);
  const baseline = ind.baseline !== null && ind.baseline !== undefined && ind.baseline !== '' ? Number(ind.baseline) : null;
  const realizado = Number(ultima.valor_realizado);

  let pct;
  if (baseline !== null && baseline !== meta) {
    maiorEhMelhor = meta > baseline;     // com baseline e meta, a conta já define o sentido
    pct = maiorEhMelhor
      ? ((realizado - baseline) / (meta - baseline)) * 100
      : ((baseline - realizado) / (baseline - meta)) * 100;
  } else if (!maiorEhMelhor) {
    pct = realizado > 0 ? (meta / realizado) * 100 : 100;   // sem baseline e "menor é melhor"
  } else {
    pct = (realizado / meta) * 100;
  }
  pct = Math.max(0, Math.round(pct));
  const pctExibido = Math.min(pct, 100);

  const cor = pct >= 90 ? '#16A34A' : pct >= 60 ? '#D97706' : '#DC2626';
  return { pct, pctExibido, cor, maiorEhMelhor };
}

// Medições da mais recente para a mais antiga. Quando há mais de uma no mesmo mês, vale o lançamento mais recente.
function ordenarMedicoes(medicoes) {
  return [...(medicoes || [])].sort(
    (a, b) => String(b.mes_referencia).localeCompare(String(a.mes_referencia)) || (b.id - a.id)
  );
}

// Anel do prazo: enche conforme o tempo passa entre a criação do indicador e o prazo da meta.
function calcularAnelPrazo(ind, metaAtingida) {
  if (!ind.prazo_meta) return { pct: 0, texto: '—', label: 'sem prazo', cor: '#94A3B8' };
  const fim = dayjs(ind.prazo_meta).startOf('day');
  const hoje = dayjs().startOf('day');
  const inicio = ind.criado_em ? dayjs(ind.criado_em).startOf('day') : hoje;
  const restantes = fim.diff(hoje, 'day');
  const total = Math.max(1, fim.diff(inicio, 'day'));
  const pct = Math.min(100, Math.max(0, Math.round(((total - restantes) / total) * 100)));
  if (metaAtingida) return { pct: 100, texto: 'Meta', label: 'atingida', cor: '#16A34A' };
  if (restantes < 0) return { pct: 100, texto: String(-restantes), label: 'em atraso', cor: '#DC2626' };
  if (restantes === 0) return { pct, texto: 'Hoje', label: 'vence', cor: '#D97706' };
  return { pct, texto: String(restantes), label: restantes === 1 ? 'dia' : 'dias', cor: restantes <= 30 ? '#D97706' : '#13375F' };
}

function calcularTendencia(medicoes, maiorEhMelhor) {
  if (!medicoes || medicoes.length < 2) return null;
  const atual = Number(medicoes[0].valor_realizado);
  const anterior = Number(medicoes[1].valor_realizado);
  const delta = atual - anterior;
  if (delta === 0) return null;
  const melhorou = maiorEhMelhor ? delta > 0 : delta < 0;
  return { delta, melhorou };
}

const pagamentoConfig = {
  sim:      { label: 'Pago',      color: 'success', bg: '#EAF3DE' },
  nao:      { label: 'Em aberto', color: 'error',   bg: '#FCEBEB' },
  pendente: { label: 'Pendente',  color: 'warning', bg: '#FAEEDA' },
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function EmDesenvolvimento({ nome }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
          backgroundColor: '#eef0f3', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ConstructionIcon sx={{ fontSize: 28, color: '#94a3b8' }} />
      </Box>
      <Typography variant="h6" color="text.secondary" mb={1}>
        {nome}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Esta seção está sendo desenvolvida e estará disponível em breve.
      </Typography>
    </Box>
  );
}

function CardCarga({ titulo, contratada, planejada, realizada, planAcum, realAcum, cor, formatarValor, usadoNoContrato }) {
  const formatar = formatarValor || formatDias;
  // Projeto de contrato: o anel e o saldo contam o que o contrato inteiro já usou (carga compartilhada).
  const usado = usadoNoContrato !== undefined && usadoNoContrato !== null ? usadoNoContrato : realAcum;
  const pctGeral = contratada > 0 ? Math.min(Math.round((usado / contratada) * 100), 100) : 0;
  const saldo = contratada - usado;
  const diferenca = realAcum - planAcum;

  return (
    <Card sx={{ mb: 2.5, borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ backgroundColor: cor, px: 3, py: 1.5 }}>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>{titulo}</Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <AnelProgresso
            pct={pctGeral} tamanho={100}
            corAnel={cor} corTrilha="rgba(0,0,0,0.08)"
            corFundoInterno="#fbfbfd" corTexto="#1a1a2e" corLabel="#94a3b8"
          />
          <Box sx={{ flex: 1, minWidth: 220, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 2 }}>
            <MiniStat label="Contratada" valor={formatar(contratada)} />
            <MiniStat label="Planejada (mês)" valor={formatar(planejada)} />
            <MiniStat label="Realizada (mês)" valor={formatar(realizada)} cor={cor} />
            <MiniStat label="Saldo" valor={formatar(saldo)} cor={saldo < 0 ? '#DC2626' : '#16A34A'} />
          </Box>
        </Box>

        <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', fontWeight: 700, mt: 3, mb: 0.5 }}>
          Acumulado — realizado vs. planejado
        </Typography>
        <BarraComparativa realizado={realAcum} planejado={planAcum} total={contratada} cor={cor} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
            {formatar(realAcum)} / {formatar(contratada)}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: diferenca >= 0 ? '#16A34A' : '#DC2626' }}>
            {diferenca >= 0 ? `▲ ${formatar(Math.abs(diferenca))} à frente` : `▼ ${formatar(Math.abs(diferenca))} atrás`} do planejado
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function CardWorkshops({ vendidos, consumidos, workshops }) {
  const pct = vendidos > 0 ? Math.min(Math.round((consumidos / vendidos) * 100), 100) : 0;
  const restantes = Math.max((vendidos || 0) - (consumidos || 0), 0);

  return (
    <Card sx={{ mb: 2.5, borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ backgroundColor: '#e94560', px: 3, py: 1.5 }}>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>Workshop</Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <AnelProgresso
            pct={pct} tamanho={100}
            corAnel="#e94560" corTrilha="rgba(0,0,0,0.08)"
            corFundoInterno="#fbfbfd" corTexto="#1a1a2e" corLabel="#94a3b8"
          />
          <Box sx={{ flex: 1, minWidth: 220, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 2 }}>
            <MiniStat label="Vendidos" valor={vendidos || 0} />
            <MiniStat label="Consumidos" valor={consumidos || 0} cor="#e94560" />
            <MiniStat label="Restantes" valor={restantes} cor={restantes === 0 && vendidos > 0 ? '#16A34A' : '#1a1a2e'} />
          </Box>
        </Box>

        {(!workshops || workshops.length === 0) ? (
          <Typography color="text.secondary" sx={{ mt: 3, fontSize: 13 }}>
            Nenhum workshop cadastrado ainda para este projeto — o Comercial cadastra na hora da venda.
          </Typography>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', fontWeight: 700, mb: 1.5 }}>
              Workshops deste projeto
            </Typography>
            {workshops.map((w) => (
              <Box
                key={w.id}
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  py: 1.2, px: 1.5, mb: 1, borderRadius: 2,
                  backgroundColor: w.consumido ? '#fce8ec' : '#f8f9fa',
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1a1a2e' }}>
                    {w.nome || 'Ainda sem nome'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                    {w.membros_equipe_nomes?.length > 0 ? w.membros_equipe_nomes.join(', ') : 'Ainda não iniciado'}
                  </Typography>
                </Box>
                <Box sx={{
                  fontSize: 11, fontWeight: 700, px: 1, py: 0.3, borderRadius: 1,
                  color: w.consumido ? '#e94560' : '#94a3b8',
                  backgroundColor: w.consumido ? 'rgba(233,69,96,0.12)' : 'rgba(148,163,184,0.15)',
                }}>
                  {w.consumido ? 'CONSUMIDO' : 'PENDENTE'}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProjetoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isGestor, temAcesso } = useAuth();
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');

  // Iniciar projeto (Planejamento -> Em andamento)
  const [confirmarInicio, setConfirmarInicio] = useState(false);
  const [iniciando, setIniciando] = useState(false);
  const [avisoInicio, setAvisoInicio] = useState(null);

  // Concluir projeto (Em andamento ou Pausado -> Concluído)
  const [confirmarConclusao, setConfirmarConclusao] = useState(false);
  const [concluindo, setConcluindo] = useState(false);

  // Editar projeto
  const [abrirModalProjeto, setAbrirModalProjeto] = useState(false);
  const [formProjeto, setFormProjeto] = useState({
    nome: '', data_inicio: '', data_fim: '', membros: [], escopo: '', sponsor: '', coordenador_corporativo: '',
  });
  const [opcoesEditar, setOpcoesEditar] = useState({ membros: [] });
  const planejamentoDoEditar = useRef(null);      // { validar, gravar, editavel } registrado pela grade do planejamento

  // Editar equipe do projeto (célula + membros da equipe alocados)
  const [abrirModalAlocacao, setAbrirModalAlocacao] = useState(false);
  const [membrosDisponiveis, setMembrosDisponiveis] = useState([]);
  const [membrosSelecionados, setMembrosSelecionados] = useState([]);
  const [celulasDisponiveis, setCelulasDisponiveis] = useState([]);
  const [celulaSelecionada, setCelulaSelecionada] = useState(null);

  // Indicadores
  const [abrirModalIndicador, setAbrirModalIndicador] = useState(false);
  const [indicadorEditando, setIndicadorEditando] = useState(null);
  const [formIndicador, setFormIndicador] = useState({
    nome: '', unidade: '', baseline: '', meta: '', prazo_meta: '', sentido: '', ativo: true,
  });

  // Medições
  const [abrirModalMedicao, setAbrirModalMedicao] = useState(false);
  const [indicadorSelecionado, setIndicadorSelecionado] = useState(null);
  const [medicaoEditando, setMedicaoEditando] = useState(null);
  const [evolucaoId, setEvolucaoId] = useState(null);   // janela "Evolução" (gráfico + histórico completo)
  const [formMedicao, setFormMedicao] = useState({
    mes_referencia: dayjs().format('YYYY-MM-01'),
    valor_realizado: '',
    observacao: '',
  });

  // Confirmações de exclusão (janelas do sistema, no lugar da caixa do navegador)
  const [excluirIndicador, setExcluirIndicador] = useState(null);   // indicador a excluir
  const [excluirMedicao, setExcluirMedicao] = useState(null);       // medição a excluir
  const [excluindoItem, setExcluindoItem] = useState(false);

  const carregarProjeto = () => {
    api.get(`/projetos/${id}/`).then((res) => {
      setProjeto(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { carregarProjeto(); }, [id]);

  const handleAbrirEditarProjeto = () => {
    setErro('');
    planejamentoDoEditar.current = null;
    api.get('/contratos/opcoes-projeto/').then((res) => setOpcoesEditar(res.data)).catch(() => {});
    setFormProjeto({
      nome: projeto.nome || '',
      data_inicio: projeto.data_inicio || '',
      data_fim: projeto.data_fim || '',
      membros: projeto.membros_equipe || [],
      escopo: projeto.escopo || '',
      sponsor: projeto.sponsor || '',
      coordenador_corporativo: projeto.coordenador_corporativo || '',
    });
    setAbrirModalProjeto(true);
  };

    const handleAbrirAlocacao = () => {
    setErro('');
    api.get('/membros-equipe/').then((res) => {
      setMembrosDisponiveis(res.data.filter((c) => c.ativo !== false));
    }).catch(() => {});
    api.get('/celulas/').then((res) => {
      setCelulasDisponiveis(res.data);
    }).catch(() => {});
    setMembrosSelecionados(projeto.membros_equipe || []);
    setCelulaSelecionada(projeto.celula || null);
    setAbrirModalAlocacao(true);
  };

  const handleSalvarAlocacao = async () => {
    setErro('');
    try {
      await api.patch(`/projetos/${id}/`, {
        membros_equipe: membrosSelecionados,
        celula: celulaSelecionada,
      });
      setSucesso('Equipe do projeto atualizada com sucesso!');
      setAbrirModalAlocacao(false);
      carregarProjeto();
    } catch (err) {
      const detalhe = err?.response?.data;
      const primeiroCampo = detalhe && typeof detalhe === 'object' ? Object.values(detalhe)[0] : null;
      setErro(
        (Array.isArray(primeiroCampo) ? primeiroCampo[0] : primeiroCampo) ||
        detalhe?.detail ||
        'Erro ao atualizar a equipe do projeto.'
      );
    }
  };

  const handleSalvarProjeto = async () => {
    setErro('');
    if (!formProjeto.nome.trim()) { setErro('O nome do projeto é obrigatório.'); return; }
    if (formProjeto.data_inicio && formProjeto.data_fim && formProjeto.data_fim < formProjeto.data_inicio) {
      setErro('A data fim não pode ser antes da data início.'); return;
    }
    const plano = planejamentoDoEditar.current;
    const erroPlano = plano && plano.editavel ? plano.validar() : '';
    if (erroPlano) { setErro(`Planejamento da carga — ${erroPlano}`); return; }
    try {
      await api.patch(`/projetos/${id}/`, {
        nome: formProjeto.nome.trim(),
        data_inicio: formProjeto.data_inicio || null,
        data_fim: formProjeto.data_fim || null,
        membros_equipe: formProjeto.membros,
        escopo: formProjeto.escopo,
        sponsor: formProjeto.sponsor,
        coordenador_corporativo: formProjeto.coordenador_corporativo,
      });
    } catch (err) {
      const d = err?.response?.data;
      const primeiro = d && typeof d === 'object' ? Object.values(d)[0] : null;
      setErro((Array.isArray(primeiro) ? primeiro[0] : primeiro) || 'Erro ao atualizar informações do projeto.');
      return;
    }
    if (plano && plano.editavel) {
      const falha = await plano.gravar();
      if (falha) {
        setErro(`Os dados do projeto foram salvos, mas o planejamento não: ${falha}`);
        carregarProjeto();
        return;
      }
    }
    setSucesso('Projeto atualizado com sucesso!');
    setAbrirModalProjeto(false);
    carregarProjeto();
  };

  const handleIniciarProjeto = async () => {
    setIniciando(true);
    try {
      await api.post(`/projetos/${id}/iniciar/`);
      setConfirmarInicio(false);
      setAvisoInicio({ tipo: 'success', texto: 'Projeto iniciado! Ele agora aparece no Controle de Carga.' });
      carregarProjeto();
    } catch (err) {
      setConfirmarInicio(false);
      setAvisoInicio({ tipo: 'error', texto: err?.response?.data?.detail || 'Não foi possível iniciar o projeto.' });
    } finally {
      setIniciando(false);
    }
  };

  const handleConcluirProjeto = async () => {
    setConcluindo(true);
    try {
      await api.post(`/projetos/${id}/concluir/`);
      setConfirmarConclusao(false);
      setAvisoInicio({ tipo: 'success', texto: 'Projeto concluído! Ele não aparece mais na lista de projetos da Agenda.' });
      carregarProjeto();
    } catch (err) {
      setConfirmarConclusao(false);
      setAvisoInicio({ tipo: 'error', texto: err?.response?.data?.detail || 'Não foi possível concluir o projeto.' });
    } finally {
      setConcluindo(false);
    }
  };

  const handleAbrirIndicador = (ind = null) => {
    if (ind) {
      setIndicadorEditando(ind);
      setFormIndicador({
        nome: ind.nome, unidade: ind.unidade || '',
        baseline: ind.baseline || '', meta: ind.meta || '',
        prazo_meta: ind.prazo_meta || '',
        // indicador antigo (sem escolha): já sugere o sentido que dá para deduzir da baseline e da meta
        sentido: ind.sentido
          || (ind.baseline !== null && ind.meta !== null && Number(ind.baseline) !== Number(ind.meta)
            ? (Number(ind.meta) > Number(ind.baseline) ? 'maior' : 'menor') : ''),
        ativo: ind.ativo,
      });
    } else {
      setIndicadorEditando(null);
      setFormIndicador({ nome: '', unidade: '', baseline: '', meta: '', prazo_meta: '', sentido: '', ativo: true });
    }
    setAbrirModalIndicador(true);
  };

  const handleSalvarIndicador = async () => {
    setErro('');
    if (!formIndicador.prazo_meta) {
      setErro('Informe o prazo para atingir a meta.');
      return;
    }
    if (!formIndicador.sentido) {
      setErro('Escolha o sentido do indicador: "Maior - melhor" ou "Menor - melhor".');
      return;
    }
    try {
      if (indicadorEditando) {
        await api.patch(`/indicadores/${indicadorEditando.id}/`, { ...formIndicador, projeto: id });
        setSucesso('Indicador atualizado com sucesso!');
      } else {
        await api.post('/indicadores/', { ...formIndicador, projeto: id });
        setSucesso('Indicador criado com sucesso!');
      }
      setAbrirModalIndicador(false);
      carregarProjeto();
    } catch (err) {
      const d = err?.response?.data;
      setErro(d?.prazo_meta?.[0] || d?.sentido?.[0] || 'Erro ao salvar indicador.');
    }
  };

  const handleDeletarIndicador = async () => {
    if (!excluirIndicador) return;
    setExcluindoItem(true);
    setErro('');
    try {
      await api.delete(`/indicadores/${excluirIndicador.id}/`);
      setSucesso('Indicador excluído.');
      carregarProjeto();
    } catch { setErro('Erro ao excluir indicador.'); }
    setExcluirIndicador(null);
    setExcluindoItem(false);
  };

  const mensagemDoErro = (err, padrao) => {
    const dados = err?.response?.data;
    const primeiro = dados && typeof dados === 'object' ? Object.values(dados)[0] : null;
    return dados?.detail || (Array.isArray(primeiro) ? primeiro[0] : primeiro) || padrao;
  };

  // medicao = null lança uma nova; com uma medição, abre para corrigir
  const handleAbrirMedicao = (ind, medicao = null) => {
    setErro('');
    setIndicadorSelecionado(ind);
    setMedicaoEditando(medicao);
    setFormMedicao(medicao ? {
      mes_referencia: medicao.mes_referencia,
      valor_realizado: medicao.valor_realizado,
      observacao: medicao.observacao || '',
    } : {
      mes_referencia: dayjs().format('YYYY-MM-01'),
      valor_realizado: '',
      observacao: '',
    });
    setAbrirModalMedicao(true);
  };

  const handleSalvarMedicao = async () => {
    setErro('');
    try {
      if (medicaoEditando) {
        await api.patch(`/medicoes/${medicaoEditando.id}/`, formMedicao);
        setSucesso('Medição corrigida com sucesso!');
      } else {
        await api.post('/medicoes/', { ...formMedicao, indicador: indicadorSelecionado.id });
        setSucesso('Medição lançada com sucesso!');
      }
      setAbrirModalMedicao(false);
      carregarProjeto();
    } catch (err) { setErro(mensagemDoErro(err, 'Erro ao salvar a medição.')); }
  };

  const handleExcluirMedicao = async () => {
    if (!excluirMedicao) return;
    setExcluindoItem(true);
    setErro('');
    try {
      await api.delete(`/medicoes/${excluirMedicao.id}/`);
      setSucesso('Medição excluída.');
      carregarProjeto();
    } catch (err) { setErro(mensagemDoErro(err, 'Erro ao excluir a medição.')); }
    setExcluirMedicao(null);
    setExcluindoItem(false);
  };

  if (loading) return <Carregando />;
  if (!projeto) return <Box><Typography color="error">Projeto não encontrado.</Typography></Box>;

  const st = statusConfig[projeto.status] || statusConfig.planejamento;
  // Projeto de contrato novo: o anel mostra o quanto do contrato (pool único) já foi usado.
  const info = projeto.contrato_info;
  const pctCargaGeral = info
    ? (info.carga_total > 0 ? Math.min(Math.round((info.carga_usada / info.carga_total) * 100), 100) : 0)
    : (projeto.carga_total_contratada > 0
      ? Math.min(Math.round((projeto.realizado_acumulado / projeto.carga_total_contratada) * 100), 100)
      : 0);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projetos')} sx={{ color: '#1a1a2e', mb: 2 }}>
        Voltar
      </Button>

      <Box
        sx={{
          position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3,
          background: 'linear-gradient(135deg, #13375F 0%, #0d2642 100%)',
          backgroundImage: `
            repeating-linear-gradient(45deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 26px),
            linear-gradient(135deg, #13375F 0%, #0d2642 100%)
          `,
          p: { xs: 2.5, sm: 3.5 },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
          <AnelProgresso pct={pctCargaGeral} />

          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                {projeto.nome}
              </Typography>
              <Box
                sx={{
                  backgroundColor: statusHex[projeto.status] || statusHex.planejamento,
                  color: 'white', px: 1.1, py: 0.25, borderRadius: 1.5,
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
                }}
              >
                {st.label}
              </Box>
              {projeto.status === 'planejamento' && temAcesso('iniciar_projeto') && (
                <Button
                  size="small" variant="contained" startIcon={<PlayArrowIcon sx={{ fontSize: 17 }} />}
                  onClick={() => setConfirmarInicio(true)}
                  sx={{
                    ml: 0.5, backgroundColor: '#e94560', textTransform: 'none', fontWeight: 700,
                    '&:hover': { backgroundColor: '#c93a52' },
                  }}
                >
                  Iniciar projeto
                </Button>
              )}
              {['em_andamento', 'pausado'].includes(projeto.status) && temAcesso('concluir_projeto') && (
                <Button
                  size="small" variant="contained" startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: 17 }} />}
                  onClick={() => setConfirmarConclusao(true)}
                  sx={{
                    ml: 0.5, backgroundColor: '#16A34A', textTransform: 'none', fontWeight: 700,
                    '&:hover': { backgroundColor: '#15803D' },
                  }}
                >
                  Concluir projeto
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, color: 'rgb(255, 255, 255)' }}>
              <BusinessIcon sx={{ fontSize: 15 }} />
              <Typography variant="body2">{projeto.cliente_nome}</Typography>
            </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, color: 'rgb(255, 255, 255)' }}>
              <WorkspacesIcon sx={{ fontSize: 15 }} />
              <Typography variant="body2">
                Célula: {projeto.celula_nome || 'não definida'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 2 }}>
              <GroupsIcon sx={{ fontSize: 15, color: 'rgb(255, 254, 254)', mt: 0.4 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {projeto.membros_equipe_nomes?.length > 0 ? (
                  projeto.membros_equipe_nomes.map((nome, i) => (
                    <Box
                      key={i}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.12)', color: 'white',
                        px: 1, py: 0.3, borderRadius: 1, fontSize: 12,
                      }}
                    >
                      {nome}
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: 'rgb(255, 255, 255)', fontSize: 13 }}>
                    Nenhum membro da equipe alocado
                  </Typography>
                )}
                {isGestor && (
                  <IconButton size="small" onClick={handleAbrirAlocacao} sx={{ p: 0.3, color: 'rgb(255, 255, 255)' }}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'rgb(255, 255, 255)' }}>
              <CalendarTodayIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                {projeto.data_inicio ? dayjs(projeto.data_inicio).format('DD/MM/YYYY') : '—'}
                {' → '}
                {projeto.data_fim ? dayjs(projeto.data_fim).format('DD/MM/YYYY') : '—'}
              </Typography>
            </Box>
          </Box>

          {projeto.pode_editar_projeto && (
            <Button
              size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 15 }} />}
              onClick={handleAbrirEditarProjeto}
              sx={{ color: 'white', borderColor: 'rgb(255, 255, 255)', alignSelf: 'flex-start' }}
            >
              Editar
            </Button>
          )}
        </Box>

        {(projeto.escopo || projeto.sponsor || projeto.coordenador_corporativo) && (
          <Box
            sx={{
              mt: 3, pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.1)',
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2,
            }}
          >
            {projeto.escopo && (
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(255, 255, 255)', fontWeight: 600 }}>
                  Escopo
                </Typography>
                <Typography sx={{ color: 'white', fontSize: 13 }}>{projeto.escopo}</Typography>
              </Box>
            )}
            {projeto.sponsor && (
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(255, 255, 255)', fontWeight: 600 }}>
                  Sponsor
                </Typography>
                <Typography sx={{ color: 'white', fontSize: 13 }}>{projeto.sponsor}</Typography>
              </Box>
            )}
            {projeto.coordenador_corporativo && (
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(255, 255, 255)', fontWeight: 600 }}>
                  Coordenador corporativo
                </Typography>
                <Typography sx={{ color: 'white', fontSize: 13 }}>{projeto.coordenador_corporativo}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <AbasProjeto
        valor={tab} onChange={setTab}
        grupos={[
          {
            titulo: 'Acompanhar', cor: '#13375F',
            itens: [
              { indice: 0, label: 'Carga', icone: <SpeedIcon /> },
              { indice: 1, label: 'Indicadores', icone: <TrendingUpIcon /> },
              { indice: 2, label: 'Acompanhamento', icone: <AssignmentIcon /> },
              { indice: 3, label: 'Histórico', icone: <HistoryIcon />, breve: true },
            ],
          },
          {
            titulo: 'Gestão e registros', cor: '#e94560',
            itens: [
              { indice: 4, label: 'Financeiro', icone: <PaidIcon />, breve: true },
              { indice: 7, label: 'Relatórios de Visitas', icone: <DescriptionIcon /> },
              { indice: 5, label: 'Fotos', icone: <PhotoLibraryIcon /> },
              { indice: 6, label: 'Roadmap', icone: <MapIcon />, breve: true },
            ],
          },
        ]}
      />

      {/* ABA CARGA */}
      <TabPanel value={tab} index={0}>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {info && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Este projeto faz parte do contrato <strong>{info.nome}</strong>, que tem {info.quantidade_projetos}{' '}
            {info.quantidade_projetos === 1 ? 'projeto consumindo' : 'projetos consumindo'} da mesma carga.
          </Typography>
        )}
        <CardCarga titulo="Consultoria"
          contratada={info ? info.carga_total : projeto.carga_total_contratada} planejada={projeto.carga_planejada}
          realizada={projeto.carga_realizada} planAcum={projeto.planejado_acumulado}
          realAcum={projeto.realizado_acumulado} cor="#1a1a2e"
          usadoNoContrato={info ? info.carga_usada : undefined} />
        <CardCarga titulo="Workshop"
          contratada={info ? info.workshops_vendidos : projeto.workshops_vendidos} planejada={projeto.workshops_planejados_mes}
          realizada={projeto.workshops_realizados_mes} planAcum={projeto.workshops_planejados}
          realAcum={projeto.workshops_realizados} cor="#e94560"
          formatarValor={(v) => `${v || 0}`}
          usadoNoContrato={info ? info.workshops_realizados : undefined} />
        <PlanejamentoProjeto projetoId={projeto.id} dataInicio={projeto.data_inicio} dataFim={projeto.data_fim} />
      </TabPanel>

      {/* ABA INDICADORES */}
      <TabPanel value={tab} index={1}>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => handleAbrirIndicador()}
            sx={{ backgroundColor: '#13375F' }}>
            Novo indicador
          </Button>
        </Box>

        {projeto.indicadores?.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary" textAlign="center">
              Nenhum indicador cadastrado. Clique em "Novo indicador" para começar.
            </Typography>
          </CardContent></Card>
        ) : (
          <Grid container spacing={2}>
            {projeto.indicadores?.map((ind) => {
              const medicoes = ordenarMedicoes(ind.medicoes);
              const ultima = medicoes[0];
              const { pct, cor, maiorEhMelhor } = calcularProgresso(ind, ultima);
              const tendencia = calcularTendencia(medicoes, maiorEhMelhor);
              const inativo = ind.ativo === false;
              const corCard = inativo ? '#94A3B8' : cor;   // indicador inativo fica cinza
              const anelPrazo = calcularAnelPrazo(ind, pct !== null && pct >= 100);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={ind.id}>
                  <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <Box sx={{ backgroundColor: corCard, px: 2.5, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{ind.nome}</Typography>
                        {inativo && (
                          <Box sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: 'white', px: 0.9, py: 0.15, borderRadius: 1, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em' }}>
                            INATIVO
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        <IconButton size="small" onClick={() => handleAbrirIndicador(ind)} sx={{ color: 'white', p: 0.4 }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setExcluirIndicador(ind)} sx={{ color: 'white', p: 0.4 }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                        <AnelProgresso
                          pct={anelPrazo.pct} texto={anelPrazo.texto} label={anelPrazo.label} tamanho={78} espessura={7}
                          corAnel={inativo ? '#94A3B8' : anelPrazo.cor} corTrilha="rgba(0,0,0,0.08)"
                          corFundoInterno="#fbfbfd" corTexto="#1a1a2e" corLabel="#94a3b8"
                        />
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <MiniStat label="Baseline" valor={ind.baseline ?? '—'} />
                          <MiniStat label="Meta" valor={ind.meta ?? '—'} />
                          <MiniStat label="Prazo" valor={ind.prazo_meta ? dayjs(ind.prazo_meta).format('DD/MM/YYYY') : '—'} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#64748b' }}>
                            {maiorEhMelhor ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.03em' }}>
                              {maiorEhMelhor ? 'MAIOR - MELHOR' : 'MENOR - MELHOR'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.5, pt: 1.5, borderTop: '1px solid #eef0f3' }}>
                        <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', fontWeight: 700 }}>
                          Último realizado
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: corCard }}>
                          {ultima ? `${ultima.valor_realizado} ${ind.unidade || ''}` : '—'}
                        </Typography>
                        {tendencia && (
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: tendencia.melhorou ? '#16A34A' : '#DC2626' }}>
                            {tendencia.delta > 0 ? '▲' : '▼'} {Math.round(Math.abs(tendencia.delta) * 100) / 100}
                          </Typography>
                        )}
                      </Box>
                      {pct !== null && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                          {pct}% da meta atingida{pct > 100 ? ' (superada)' : ''}
                        </Typography>
                      )}

                      <Button size="small" startIcon={<AddCircleIcon />}
                        onClick={() => handleAbrirMedicao(ind)}
                        sx={{ color: '#1a1a2e', mt: 2 }} fullWidth variant="outlined">
                        Lançar medição
                      </Button>

                      <Button size="small" startIcon={<TrendingUpIcon />}
                        onClick={() => setEvolucaoId(ind.id)}
                        sx={{ color: '#13375F', mt: 1 }} fullWidth>
                        Ver evolução e histórico{ind.medicoes?.length ? ` (${ind.medicoes.length})` : ''}
                      </Button>

                      {ind.medicoes?.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Últimas medições
                          </Typography>
                          <Table size="small" sx={{ mt: 0.5 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: 11 }}>Mês</TableCell>
                                <TableCell sx={{ fontSize: 11 }} align="right">Valor</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {medicoes.slice(0, 3).map((m) => (
                                <TableRow key={m.id}>
                                  <TableCell sx={{ fontSize: 11 }}>
                                    {dayjs(m.mes_referencia).format('MM/YYYY')}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11 }} align="right">
                                    {m.valor_realizado} {ind.unidade}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}

                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Modal Indicador */}
        <Dialog open={abrirModalIndicador} onClose={() => setAbrirModalIndicador(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 0.5 }}>{indicadorEditando ? 'Editar indicador' : 'Novo indicador'}</DialogTitle>
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
            {indicadorEditando ? 'Atualize os dados deste indicador' : 'Defina o indicador que será acompanhado neste projeto'}
          </Typography>
          <DialogContent>
            {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

            <SecaoFormulario titulo="Dados do indicador" icone={<TrendingUpIcon sx={{ fontSize: 17, color: '#16A34A' }} />} cor="#16A34A">
              <Grid size={12}>
                <TextField fullWidth label="Nome do indicador"
                  value={formIndicador.nome}
                  onChange={(e) => setFormIndicador({ ...formIndicador, nome: e.target.value })}
                  placeholder="Ex: MOD, OEE, Lead Time..." />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Unidade"
                  value={formIndicador.unidade}
                  onChange={(e) => setFormIndicador({ ...formIndicador, unidade: e.target.value })}
                  placeholder="Ex: %, dias, R$..." />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Baseline" type="number"
                  value={formIndicador.baseline}
                  onChange={(e) => setFormIndicador({ ...formIndicador, baseline: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Meta" type="number"
                  value={formIndicador.meta}
                  onChange={(e) => setFormIndicador({ ...formIndicador, meta: e.target.value })} />
              </Grid>
              <Grid size={12}>
                <TextField fullWidth required label="Prazo para atingir a meta" type="date"
                  value={formIndicador.prazo_meta}
                  onChange={(e) => setFormIndicador({ ...formIndicador, prazo_meta: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }} />
              </Grid>
              <Grid size={12}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
                  Qual é o sentido da melhora deste indicador?
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    { valor: 'maior', Icone: ArrowUpwardIcon, titulo: 'MAIOR - MELHOR' },
                    { valor: 'menor', Icone: ArrowDownwardIcon, titulo: 'MENOR - MELHOR' },
                  ].map(({ valor, Icone, titulo }) => {
                    const marcado = formIndicador.sentido === valor;
                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={valor}>
                        <Box
                          component="button" type="button" role="radio" aria-checked={marcado}
                          onClick={() => { setErro(''); setFormIndicador({ ...formIndicador, sentido: valor }); }}
                          sx={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left',
                            p: 1.5, borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
                            border: marcado ? '2px solid #13375F' : '1px solid #cbd5e1',
                            backgroundColor: marcado ? '#eaf0f7' : 'white',
                            '&:hover': { backgroundColor: marcado ? '#eaf0f7' : '#f8fafc' },
                            '&:focus-visible': { outline: '2px solid #13375F', outlineOffset: 2 },
                          }}
                        >
                          <Box sx={{
                            width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: marcado ? '#13375F' : '#e2e8f0', color: marcado ? 'white' : '#64748b',
                          }}>
                            <Icone />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#1a1a2e' }}>{titulo}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
              {indicadorEditando && (
                <Grid size={12}>
                  <TextField fullWidth select label="Status"
                    value={formIndicador.ativo}
                    onChange={(e) => setFormIndicador({ ...formIndicador, ativo: e.target.value })}>
                    <MenuItem value={true}>Ativo</MenuItem>
                    <MenuItem value={false}>Inativo</MenuItem>
                  </TextField>
                </Grid>
              )}
            </SecaoFormulario>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAbrirModalIndicador(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSalvarIndicador} sx={{ backgroundColor: '#13375F' }}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Medição */}
        <Dialog open={abrirModalMedicao} onClose={() => setAbrirModalMedicao(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 0.5 }}>{medicaoEditando ? 'Corrigir medição' : 'Lançar medição'}</DialogTitle>
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
            {indicadorSelecionado?.nome}
          </Typography>
          <DialogContent>
            {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

            <SecaoFormulario titulo="Dados da medição" icone={<AddCircleOutlineIcon sx={{ fontSize: 17, color: '#2563EB' }} />} cor="#2563EB">
              <Grid item xs={12}>
                <TextField fullWidth label="Mês de referência" type="date"
                  value={formMedicao.mes_referencia}
                  onChange={(e) => setFormMedicao({ ...formMedicao, mes_referencia: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Selecione qualquer dia do mês" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={`Valor realizado (${indicadorSelecionado?.unidade || ''})`}
                  type="number" value={formMedicao.valor_realizado}
                  onChange={(e) => setFormMedicao({ ...formMedicao, valor_realizado: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Observação (opcional)"
                  multiline rows={2} value={formMedicao.observacao}
                  onChange={(e) => setFormMedicao({ ...formMedicao, observacao: e.target.value })} />
              </Grid>
            </SecaoFormulario>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAbrirModalMedicao(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSalvarMedicao} sx={{ backgroundColor: '#13375F' }}>
              {medicaoEditando ? 'Salvar' : 'Lançar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Janela Evolução (gráfico + histórico completo) */}
        <EvolucaoIndicador
          aberto={evolucaoId !== null}
          indicador={(projeto.indicadores || []).find((i) => i.id === evolucaoId) || null}
          aoFechar={() => { setEvolucaoId(null); setErro(''); }}
          aoLancar={(ind) => handleAbrirMedicao(ind)}
          aoEditarMedicao={(ind, m) => handleAbrirMedicao(ind, m)}
          aoExcluirMedicao={(m) => setExcluirMedicao(m)}
          erro={abrirModalMedicao ? '' : erro}
          aoFecharErro={() => setErro('')}
        />
      </TabPanel>

      {/* ABA ACOMPANHAMENTO */}
      <TabPanel value={tab} index={2}>
        <AcompanhamentoProjeto
          projetoId={id}
          acompanhamentos={projeto.acompanhamentos}
          aoAtualizar={carregarProjeto}
        />
      </TabPanel>

      {/* ABA HISTÓRICO */}
      <TabPanel value={tab} index={3}>
        <EmDesenvolvimento nome="Histórico" />
      </TabPanel>

      {/* ABA FINANCEIRO */}
      <TabPanel value={tab} index={4}>
        <EmDesenvolvimento nome="Financeiro" />
      </TabPanel>

      {/* ABA FOTOS */}
      <TabPanel value={tab} index={5}>
        <Card sx={{ borderRadius: 3, p: 2.5 }}>
          <GaleriaFotos projeto_id={id} titulo="Fotos do projeto" />
        </Card>
      </TabPanel>

      {/* ABA ROADMAP */}
      <TabPanel value={tab} index={6}>
        <EmDesenvolvimento nome="Roadmap" />
      </TabPanel>

      {/* ABA RELATÓRIOS DE VISITAS */}
      <TabPanel value={tab} index={7}>
        <RelatorioVisita projetoId={id} projeto={projeto} />
      </TabPanel>

      {/* Modal Editar Projeto */}
      <Dialog open={abrirModalProjeto} onClose={() => setAbrirModalProjeto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar projeto</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                fullWidth required label="Nome do projeto" value={formProjeto.nome}
                onChange={(e) => setFormProjeto({ ...formProjeto, nome: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="date" label="Data início" value={formProjeto.data_inicio}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => setFormProjeto({ ...formProjeto, data_inicio: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="date" label="Data fim" value={formProjeto.data_fim}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => setFormProjeto({ ...formProjeto, data_fim: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                multiple
                options={opcoesEditar.membros}
                value={opcoesEditar.membros.filter((m) => formProjeto.membros.includes(m.id))}
                getOptionLabel={(m) => m.nome || ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                groupBy={(m) => m.celula_nome || 'Sem célula'}
                onChange={(e, novo) => setFormProjeto({ ...formProjeto, membros: novo.map((m) => m.id) })}
                renderInput={(params) => <TextField {...params} label="Consultores do projeto" placeholder="Buscar consultor..." />}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth label="Escopo do projeto" multiline rows={2}
                value={formProjeto.escopo}
                onChange={(e) => setFormProjeto({ ...formProjeto, escopo: e.target.value })}
                placeholder="Ex: Projeto SIM (Sistema de Manufatura)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Sponsor do projeto"
                value={formProjeto.sponsor}
                onChange={(e) => setFormProjeto({ ...formProjeto, sponsor: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Coordenador corporativo"
                value={formProjeto.coordenador_corporativo}
                onChange={(e) => setFormProjeto({ ...formProjeto, coordenador_corporativo: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              {abrirModalProjeto && (
                <PlanejamentoNoEditar
                  projetoId={projeto.id} dataInicio={formProjeto.data_inicio} dataFim={formProjeto.data_fim}
                  registrar={(funcoes) => { planejamentoDoEditar.current = funcoes; }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModalProjeto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarProjeto} sx={{ backgroundColor: '#13375F' }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

            {/* Modal Equipe do Projeto (Célula + Membros da Equipe alocados) */}
      <Dialog open={abrirModalAlocacao} onClose={() => setAbrirModalAlocacao(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Equipe do projeto — {projeto.nome}</DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <Autocomplete
            sx={{ mt: 1, mb: 2.5 }}
            options={celulasDisponiveis}
            getOptionLabel={(c) => c.nome}
            isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
            value={celulasDisponiveis.find((c) => c.id === celulaSelecionada) || null}
            onChange={(e, novoValor) => setCelulaSelecionada(novoValor?.id || null)}
            renderInput={(params) => (
              <TextField {...params} label="Célula responsável" placeholder="Selecionar célula" />
            )}
          />

          <Autocomplete
            multiple
            options={membrosDisponiveis}
            getOptionLabel={(c) => c.nome_completo}
            isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
            value={membrosDisponiveis.filter((c) => membrosSelecionados.includes(c.id))}
            onChange={(e, novosValores) => setMembrosSelecionados(novosValores.map((c) => c.id))}
            renderInput={(params) => (
              <TextField {...params} label="Membros da equipe" placeholder="Adicionar membro da equipe" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModalAlocacao(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarAlocacao} sx={{ backgroundColor: '#13375F' }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação: Iniciar projeto */}
      <Dialog open={confirmarInicio} onClose={() => { if (!iniciando) setConfirmarInicio(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Iniciar projeto?</DialogTitle>
        <DialogContent>
          <Typography>
            Iniciar o projeto <strong>{projeto.nome}</strong>? Ele passará de Planejamento para
            Em andamento e a partir de agora aparecerá no Controle de Carga.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarInicio(false)} disabled={iniciando}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleIniciarProjeto} disabled={iniciando}
            sx={{ backgroundColor: '#e94560', '&:hover': { backgroundColor: '#c93a52' } }}
          >
            {iniciando ? 'Iniciando...' : 'Iniciar projeto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação: Concluir projeto */}
      <Dialog open={confirmarConclusao} onClose={() => { if (!concluindo) setConfirmarConclusao(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Concluir projeto?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Concluir o projeto <strong>{projeto.nome}</strong>? Ele passará para Concluído e deixará de
            aparecer na lista de projetos da Agenda.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Os lançamentos já feitos e as notas fiscais continuam registrados. Se concluir por engano,
            peça ao Admin para reabrir o projeto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarConclusao(false)} disabled={concluindo}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleConcluirProjeto} disabled={concluindo}
            sx={{ backgroundColor: '#16A34A', '&:hover': { backgroundColor: '#15803D' } }}
          >
            {concluindo ? 'Concluindo...' : 'Concluir projeto'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação: Excluir indicador */}
      <Dialog open={!!excluirIndicador} onClose={() => { if (!excluindoItem) setExcluirIndicador(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir indicador?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O indicador <strong>{excluirIndicador?.nome}</strong>
            {excluirIndicador?.medicoes?.length > 0
              ? ` e as ${excluirIndicador.medicoes.length} ${excluirIndicador.medicoes.length === 1 ? 'medição lançada' : 'medições lançadas'} nele serão apagados.`
              : ' será apagado.'}
            {' '}Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluirIndicador(null)} disabled={excluindoItem}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeletarIndicador} disabled={excluindoItem}>
            {excluindoItem ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação: Excluir medição */}
      <Dialog open={!!excluirMedicao} onClose={() => { if (!excluindoItem) setExcluirMedicao(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir medição?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A medição de <strong>{excluirMedicao?.mes_referencia ? dayjs(excluirMedicao.mes_referencia).format('MM/YYYY') : ''}</strong>
            {excluirMedicao?.valor_realizado !== undefined && excluirMedicao?.valor_realizado !== null
              ? ` (${excluirMedicao.valor_realizado})` : ''}
            {' '}será apagada. Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluirMedicao(null)} disabled={excluindoItem}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleExcluirMedicao} disabled={excluindoItem}>
            {excluindoItem ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(avisoInicio)} autoHideDuration={6000} onClose={() => setAvisoInicio(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={avisoInicio?.tipo || 'success'} variant="filled" onClose={() => setAvisoInicio(null)}>
          {avisoInicio?.texto}
        </Alert>
      </Snackbar>
    </Box>
  );
}
